import { useCallback, useEffect, useMemo, useState } from 'react';
import LoadingWrapper from 'components/LoadingWrapper';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { capitalize } from 'lodash';
import { calendarClient } from 'services/calendarClient';
import { getErrorMessage } from 'utils/errorUtils';
import { errorNotification } from 'utils/notifications';
import {
  type EventMappingResult,
  type EventMappingsQuery,
  type SyncMode,
  SyncStatus,
} from '@timeedit/calendar-integration-api-contract';
import {
  Button,
  type Column,
  DefaultContentLayout,
  EditableTable,
  Select,
  TEDrawer,
  TEInput,
  Tag,
} from '@timeedit/ui-components';
import {
  Space,
  type TablePaginationConfig,
  Typography,
} from '@timeedit/ui-components/antd';
import { EventMappingDetail } from './EventMappingDetail';
import './EventMappingsPage.scss';

export function MS365EventMappingsPage() {
  const [curPage, setCurPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(false);
  const [eventMappings, setEventMappings] = useState<EventMappingResult[]>([]);
  const [selectedEventMapping, setSelectedEventMapping] =
    useState<EventMappingResult | null>(null);
  const [syncMode, setSyncMode] = useState<SyncMode>('TE_OUT');
  const [syncStatus, setSyncStatus] = useState<SyncStatus | undefined>();
  const [reservationId, setReservationId] = useState<number | undefined>();
  const [email, setEmail] = useState<string | undefined>();

  const fetchEventMappings = useCallback(async (query: EventMappingsQuery) => {
    try {
      setLoading(true);
      const response = await calendarClient.eventMapping.list({
        query,
      });
      if (response.status !== 200) {
        return errorNotification(getErrorMessage(response));
      }

      setEventMappings(response.body.results);
      setTotalResults(response.body.totalResults);
    } catch (error) {
      errorNotification(
        getErrorMessage(error) || getText('integration.calendar.logs.error')
      );
      setEventMappings([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEventMappings({
      page: curPage,
      limit,
      mode: syncMode,
      email,
      reservationId,
      syncStatus,
    });
  }, [limit, curPage, syncMode, syncStatus, fetchEventMappings]);

  const title = useTableTitle<EventMappingResult>({
    total: totalResults,
    filteredAmount: eventMappings.length,
  });

  const pagination = useMemo<TablePaginationConfig>(
    () => ({
      current: curPage,
      onChange: setCurPage,
      total: totalResults,
      pageSize: limit,
      onShowSizeChange: (_current, limit) => {
        setLimit(limit);
        setCurPage(1);
      },
      size: 'small',
      position: ['bottomCenter'],
    }),
    [curPage, totalResults, limit]
  );

  const onRowClick = useCallback((eventMapping: EventMappingResult) => {
    setSelectedEventMapping(eventMapping);
  }, []);

  const onRow = useRowProps(onRowClick, [selectedEventMapping?.id]);

  const handleFindEvent = useCallback(() => {
    fetchEventMappings({
      page: curPage,
      limit,
      mode: syncMode,
      email,
      reservationId,
      syncStatus,
    });
  }, [
    fetchEventMappings,
    curPage,
    limit,
    syncMode,
    email,
    reservationId,
    syncStatus,
  ]);

  return (
    <DefaultContentLayout
      title={getText('integration.calendar.event_mappings.title')}
      subtitle={getText('integration.calendar.event_mappings.subtitle')}
    >
      <div className="inner-content--wrapper event-mappings-page">
        <div className="event-mappings-table">
          <LoadingWrapper isLoading={loading}>
            <Space direction="vertical" className="event-mappings-filter">
              <Space>
                <Typography.Text strong>Sync mode: </Typography.Text>
                <Select
                  options={modeOptions}
                  onSelect={(val) => setSyncMode(val)}
                  value={syncMode}
                  className="sync-mode-select"
                  popupMatchSelectWidth={false}
                />
                <Select
                  options={statusOptions}
                  onChange={(val) => setSyncStatus(val)}
                  value={syncStatus}
                  className="sync-status-select"
                  allowClear
                  placeholder="Status"
                />
                <TEInput.InputNumber
                  placeholder="Reservation Id"
                  className="reservation-id-input"
                  value={reservationId}
                  onChange={(val: number | null) => {
                    setReservationId(val ? val : undefined);
                  }}
                  onPressEnter={handleFindEvent}
                />
                <TEInput.Input
                  placeholder="Email"
                  className="email-input"
                  value={email}
                  onChange={(val) => setEmail(val)}
                  onPressEnter={handleFindEvent}
                  debounceTime={0}
                  allowClear
                />
                <Button size="small" onClick={handleFindEvent}>
                  Find
                </Button>
              </Space>
              <EditableTable
                columns={columns}
                dataSource={eventMappings}
                key={'id'}
                pagination={pagination}
                rowKey={'id'}
                title={title}
                onRow={onRow}
              />
            </Space>
          </LoadingWrapper>
        </div>
        <TEDrawer
          expandWidth="70vw"
          firstDrawer={{
            title: selectedEventMapping?.reservationId.toString(),
            content: <EventMappingDetail eventMapping={selectedEventMapping} />,
            drawerOpen: !!selectedEventMapping,
            setDrawerOpen: (open) => !open && setSelectedEventMapping(null),
            extra: null,
          }}
        />
      </div>
    </DefaultContentLayout>
  );
}

const columns: Column<EventMappingResult>[] = [
  {
    key: 'reservationId',
    dataIndex: 'reservationId',
    title: 'Reservation',
    width: '100px',
  },
  {
    key: 'userPrincipalName',
    dataIndex: 'userPrincipalName',
    ellipsis: true,
    title: 'Email',
  },
  {
    key: 'updatedAt',
    dataIndex: 'updatedAt',
    ellipsis: true,
    title: 'Last updated',
  },
  {
    key: 'begin',
    dataIndex: 'begin',
    ellipsis: true,
    title: 'Reservation begin',
  },
  {
    key: 'isRecurring',
    dataIndex: 'isRecurring',
    ellipsis: true,
    title: 'Event type',
    width: '100px',
    render: (isRecurring: boolean) => (isRecurring ? 'Recurring' : 'Single'),
  },
  {
    key: 'syncStatus',
    dataIndex: 'syncStatus',
    ellipsis: true,
    title: 'Status',
    align: 'center',
    width: '100px',
    render: (val: SyncStatus) => (
      <Tag color={val === SyncStatus.SYNCED ? 'green' : 'red'}>{val}</Tag>
    ),
  },
];

const modeOptions = [
  {
    label: getText(
      'integration.calendar.sync_settings.form.sync_mode.te_out.label'
    ),
    value: 'TE_OUT',
  },
  {
    label: getText(
      'integration.calendar.sync_settings.form.sync_mode.te_in.label'
    ),
    value: 'TE_IN',
  },
];

const statusOptions = Object.values(SyncStatus).map((val) => ({
  value: val,
  label: capitalize(val),
}));
